import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ShieldCheck, LogOut, Loader2, AlertTriangle, CheckCircle2, Search,
  FileText, Receipt, Link2, Copy, RefreshCw, ArrowLeft,
} from "lucide-react";
import { adminClient, adminAuth, API, STATUS_LABEL, STATUS_COLOR } from "@/lib/api";
import OrderTimeline from "@/components/OrderTimeline";

// -------- LOGIN --------
export function AdminLogin() {
  useEffect(() => { document.title = "Admin · STK Komfort"; }, []);
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await adminClient().post("/admin/login", { password: pwd });
      adminAuth.set(data.token);
      nav("/admin/objednavky", { replace: true });
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Přihlášení selhalo.");
    } finally { setBusy(false); }
  };

  return (
    <Shell>
      <div className="max-w-md mx-auto pt-20">
        <div className="rounded-3xl border border-amber-300/20 bg-white/[0.03] p-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-300/10 border border-amber-300/30 text-amber-300">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <h1 className="font-heading mt-5 text-2xl font-bold text-white">Administrace</h1>
          <p className="mt-2 text-sm text-slate-400">Zadejte administrátorské heslo.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <input
              type="password" placeholder="Heslo"
              value={pwd} onChange={(e) => setPwd(e.target.value)} autoFocus
              data-testid="admin-pwd"
              className="w-full rounded-xl border border-white/15 bg-[#0A0D17] px-4 py-3 text-sm text-white focus:border-amber-300 focus:ring-2 focus:ring-amber-300/30 outline-none"
            />
            {err && <div data-testid="admin-error" className="rounded-xl border border-rose-400/30 bg-rose-400/10 px-3 py-2 text-xs text-rose-200 flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{err}</div>}
            <button type="submit" disabled={busy} data-testid="admin-login-submit"
              className="w-full rounded-xl bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 disabled:opacity-50 px-5 py-3 font-bold text-slate-900">
              {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Přihlásit se"}
            </button>
          </form>
        </div>
      </div>
    </Shell>
  );
}

// -------- LIST --------
export function AdminOrders() {
  useEffect(() => { document.title = "Objednávky · Admin"; }, []);
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [showShared, setShowShared] = useState(false);
  const [sharedRes, setSharedRes] = useState(null);
  const [sharedForm, setSharedForm] = useState({ service_name: "STK Komfort — osobní auto M1/N1", service_id: "stk-komfort-m1n1", service_category: "STK a emise", price_label: "3 990 Kč", amount: 3990, note: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status_filter = filter;
      if (q.trim()) params.q = q.trim();
      const { data } = await adminClient().get("/admin/orders", { params });
      setOrders(data);
    } catch (ex) {
      if (ex?.response?.status === 401) { adminAuth.clear(); nav("/admin"); }
    } finally { setLoading(false); }
  }, [filter, q, nav]);

  useEffect(() => { load(); }, [load]);

  return (
    <Shell auth>
      <Header onLogout={() => { adminAuth.clear(); nav("/admin"); }} />
      <div className="flex flex-wrap gap-3 items-center mb-5">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Hledat: jméno, e-mail, VS, č. objednávky..."
            data-testid="admin-search"
            className="w-full rounded-xl border border-white/15 bg-[#0A0D17] pl-10 pr-3 py-2.5 text-sm text-white focus:border-amber-300 focus:ring-2 focus:ring-amber-300/30 outline-none"
          />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-filter-status"
          className="rounded-xl border border-white/15 bg-[#0A0D17] px-3 py-2.5 text-sm text-white">
          <option value="">Všechny stavy</option>
          <option value="awaiting_payment">Čeká na platbu</option>
          <option value="paid">Platba přijata</option>
          <option value="invoiced">Faktura vystavena</option>
          <option value="done">Vyřízeno</option>
          <option value="cancelled">Zrušeno</option>
        </select>
        <button onClick={load} data-testid="admin-refresh"
          className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] px-4 py-2.5 text-sm text-white">
          <RefreshCw className="h-4 w-4" /> Obnovit
        </button>
        <button onClick={() => { setShowShared((v) => !v); setSharedRes(null); }} data-testid="admin-toggle-shared"
          className="inline-flex items-center gap-2 rounded-xl border border-amber-300/30 bg-amber-300/10 hover:bg-amber-300/20 px-4 py-2.5 text-sm font-semibold text-amber-200">
          <Link2 className="h-4 w-4" /> Sdílený odkaz
        </button>
      </div>

      {showShared && (
        <div className="mb-6 rounded-2xl border border-amber-300/25 bg-white/[0.03] p-5" data-testid="admin-shared-panel">
          <div className="text-xs uppercase tracking-[0.18em] text-amber-300/90 font-semibold">Vytvořit jednorázový odkaz na konkrétní službu/cenu</div>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <input value={sharedForm.service_name} onChange={(e) => setSharedForm({ ...sharedForm, service_name: e.target.value })} placeholder="Název služby"
              data-testid="admin-shared-name" className="rounded-lg border border-white/15 bg-[#0A0D17] px-3 py-2 text-sm text-white" />
            <input value={sharedForm.service_category} onChange={(e) => setSharedForm({ ...sharedForm, service_category: e.target.value })} placeholder="Kategorie"
              className="rounded-lg border border-white/15 bg-[#0A0D17] px-3 py-2 text-sm text-white" />
            <input value={sharedForm.price_label} onChange={(e) => setSharedForm({ ...sharedForm, price_label: e.target.value })} placeholder="Cenovka (např. 3 990 Kč)"
              className="rounded-lg border border-white/15 bg-[#0A0D17] px-3 py-2 text-sm text-white" />
            <input type="number" value={sharedForm.amount} onChange={(e) => setSharedForm({ ...sharedForm, amount: parseFloat(e.target.value) || 0 })} placeholder="Částka v Kč"
              data-testid="admin-shared-amount" className="rounded-lg border border-white/15 bg-[#0A0D17] px-3 py-2 text-sm text-white" />
            <input value={sharedForm.note} onChange={(e) => setSharedForm({ ...sharedForm, note: e.target.value })} placeholder="Poznámka pro klienta (volitelné)"
              className="sm:col-span-2 rounded-lg border border-white/15 bg-[#0A0D17] px-3 py-2 text-sm text-white" />
          </div>
          <button data-testid="admin-shared-create"
            onClick={async () => {
              try {
                const { data } = await adminClient().post("/admin/shared-orders", { ...sharedForm, service_id: sharedForm.service_id || "custom" });
                setSharedRes(data);
              } catch (ex) {
                setSharedRes({ error: ex?.response?.data?.detail || "Nelze vytvořit." });
              }
            }}
            className="mt-3 rounded-lg bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 px-4 py-2 text-sm font-bold text-slate-900">
            Vygenerovat odkaz
          </button>
          {sharedRes && !sharedRes.error && (
            <div className="mt-4 rounded-lg border border-emerald-400/30 bg-emerald-400/10 p-3 text-xs text-emerald-200" data-testid="admin-shared-result">
              <div className="font-semibold">Odkaz vytvořen — pošlete klientovi:</div>
              <div className="mt-2 break-all font-mono text-amber-200">{sharedRes.url}</div>
              <button onClick={() => { navigator.clipboard?.writeText(sharedRes.url); }} className="mt-2 text-amber-300 hover:text-amber-200 underline">
                Kopírovat
              </button>
            </div>
          )}
          {sharedRes?.error && (
            <div className="mt-4 rounded-lg border border-rose-400/30 bg-rose-400/10 p-3 text-xs text-rose-200">{sharedRes.error}</div>
          )}
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Načítám…</div>
      ) : orders.length === 0 ? (
        <div className="py-20 text-center text-slate-500">Žádné objednávky.</div>
      ) : (
        <div className="rounded-2xl border border-white/10 overflow-x-auto" data-testid="admin-orders-table">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-amber-300/80 bg-white/[0.02]">
              <tr>
                <Th>Č. objednávky</Th><Th>VS</Th><Th>Klient</Th><Th>Služba</Th>
                <Th className="text-right">Částka</Th><Th>Stav</Th><Th>Datum</Th><Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {orders.map((o) => (
                <tr key={o.order_number} className="hover:bg-white/[0.03]" data-testid={`admin-row-${o.order_number}`}>
                  <Td className="font-bold text-amber-200">{o.order_number}</Td>
                  <Td className="font-mono">{o.variable_symbol}</Td>
                  <Td>{o.name}<br /><span className="text-xs text-slate-500">{o.phone}{o.email ? ` · ${o.email}` : ""}</span></Td>
                  <Td className="max-w-[220px] truncate">{o.service_name}</Td>
                  <Td className="text-right font-semibold whitespace-nowrap">{o.amount.toFixed(2)} Kč</Td>
                  <Td><StatusBadge status={o.status} /></Td>
                  <Td className="whitespace-nowrap text-xs text-slate-400">{new Date(o.created_at).toLocaleString("cs-CZ")}</Td>
                  <Td>
                    <Link to={`/admin/objednavky/${o.order_number}`} className="text-amber-300 hover:text-amber-200 text-xs font-semibold" data-testid={`admin-detail-link-${o.order_number}`}>
                      Detail →
                    </Link>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Shell>
  );
}

// -------- DETAIL --------
export function AdminOrderDetail() {
  const { orderNumber } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [magic, setMagic] = useState(null);
  const [copied, setCopied] = useState("");

  useEffect(() => { document.title = `${orderNumber} · Admin`; }, [orderNumber]);

  const load = useCallback(async () => {
    try {
      const { data } = await adminClient().get("/admin/orders", { params: { q: orderNumber } });
      const found = data.find((o) => o.order_number === orderNumber);
      setOrder(found || null);
      setNote(found?.admin_note || "");
    } catch (ex) {
      if (ex?.response?.status === 401) { adminAuth.clear(); nav("/admin"); }
    }
  }, [orderNumber, nav]);

  useEffect(() => { load(); }, [load]);

  const action = async (fn, key) => {
    setErr(""); setBusy(true);
    try { await fn(); await load(); }
    catch (ex) { setErr(ex?.response?.data?.detail || `Akce ${key} selhala.`); }
    finally { setBusy(false); }
  };

  const markPaid = () => action(() => adminClient().patch(`/admin/orders/${orderNumber}`, { status: "paid", admin_note: note }), "paid");
  const markDone = () => action(() => adminClient().patch(`/admin/orders/${orderNumber}`, { status: "done", admin_note: note }), "done");
  const cancel = () => { if (!window.confirm("Zrušit objednávku?")) return; action(() => adminClient().patch(`/admin/orders/${orderNumber}`, { status: "cancelled", admin_note: note }), "cancel"); };
  const saveNote = () => action(() => adminClient().patch(`/admin/orders/${orderNumber}`, { admin_note: note }), "note");
  const issueProforma = () => action(() => adminClient().post(`/admin/orders/${orderNumber}/proforma`), "proforma");
  const issueInvoice = () => action(() => adminClient().post(`/admin/orders/${orderNumber}/invoice`), "invoice");
  const genMagic = async () => {
    setErr("");
    try { const { data } = await adminClient().post(`/admin/orders/${orderNumber}/magic-link`); setMagic(data); }
    catch (ex) { setErr(ex?.response?.data?.detail || "Nepodařilo se vygenerovat magic link."); }
  };

  const pdfUrl = (kind) => `${API}/admin/orders/${orderNumber}/${kind}.pdf`;
  const openPdf = async (kind) => {
    // axios with bearer for blob download, then open
    try {
      const res = await adminClient().get(`/admin/orders/${orderNumber}/${kind}.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (ex) {
      setErr("PDF se nepodařilo otevřít.");
    }
  };
  void pdfUrl;

  const copyText = (text, key) => {
    navigator.clipboard?.writeText(text);
    setCopied(key); setTimeout(() => setCopied(""), 1500);
  };

  if (!order) return <Shell auth><div className="py-20 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Načítám…</div></Shell>;

  return (
    <Shell auth>
      <Header onLogout={() => { adminAuth.clear(); nav("/admin"); }} />
      <Link to="/admin/objednavky" className="inline-flex items-center gap-2 text-sm text-amber-300/80 hover:text-amber-300 mb-6">
        <ArrowLeft className="h-4 w-4" /> Zpět na seznam
      </Link>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-4">
          <div className="rounded-3xl border border-amber-300/20 bg-white/[0.03] p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-amber-300/80">Objednávka</div>
                <div className="font-heading text-3xl font-extrabold text-white">{order.order_number}</div>
                <div className="mt-1 text-xs text-slate-400">VS: <span className="font-mono text-amber-200">{order.variable_symbol}</span></div>
              </div>
              <StatusBadge status={order.status} large />
            </div>

            <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
              <Field k="Zákazník" v={order.name} />
              <Field k="Telefon" v={order.phone} />
              {order.email && <Field k="E-mail" v={order.email} />}
              {order.company_name && <Field k="Firma" v={order.company_name} />}
              {order.ico && <Field k="IČO" v={order.ico} />}
              {order.dic && <Field k="DIČ" v={order.dic} />}
              <Field k="Služba" v={order.service_name} />
              <Field k="Kategorie" v={order.service_category} />
              <Field k="Částka" v={`${order.amount.toFixed(2)} Kč`} />
              {order.vehicle && <Field k="Vozidlo" v={order.vehicle} />}
              {order.spz_vin && <Field k="SPZ / VIN" v={order.spz_vin} />}
              {order.location && <Field k="Lokalita" v={order.location} />}
              {order.preferred_term && <Field k="Termín" v={order.preferred_term} />}
              {order.proforma_number && <Field k="Proforma" v={order.proforma_number} />}
              {order.invoice_number && <Field k="Faktura" v={order.invoice_number} />}
            </div>

            {order.note && (
              <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Poznámka klienta</div>
                <div className="mt-1 text-slate-200">{order.note}</div>
              </div>
            )}
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
            <div className="text-xs uppercase tracking-[0.18em] text-amber-300/80 font-semibold mb-4">Časová osa</div>
            <OrderTimeline order={order} />
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
            <div className="text-xs uppercase tracking-[0.18em] text-amber-300/80 font-semibold mb-3">Interní poznámka</div>
            <textarea
              value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              data-testid="admin-note"
              className="w-full rounded-xl border border-white/15 bg-[#0A0D17] px-4 py-3 text-sm text-white focus:border-amber-300 focus:ring-2 focus:ring-amber-300/30 outline-none resize-y"
              placeholder="Interní poznámka (jen pro admin)..."
            />
            <button onClick={saveNote} disabled={busy} data-testid="admin-save-note"
              className="mt-3 rounded-lg border border-amber-300/30 bg-amber-300/10 hover:bg-amber-300/20 px-4 py-2 text-sm font-semibold text-amber-200">
              Uložit poznámku
            </button>
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-4">
          <div className="rounded-3xl border border-amber-300/20 bg-amber-300/5 p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-amber-300/90 font-semibold mb-3">Akce</div>
            <div className="space-y-2">
              {order.status === "awaiting_payment" && (
                <ActionBtn onClick={markPaid} disabled={busy} testid="admin-mark-paid" primary>
                  <CheckCircle2 className="h-4 w-4" /> Označit jako zaplacené
                </ActionBtn>
              )}
              <ActionBtn onClick={issueProforma} disabled={busy} testid="admin-issue-proforma">
                <FileText className="h-4 w-4" /> Vystavit / přegenerovat proformu
              </ActionBtn>
              <ActionBtn onClick={() => openPdf("proforma")} testid="admin-open-proforma">
                <FileText className="h-4 w-4" /> Otevřít proformu PDF
              </ActionBtn>
              {(order.status === "paid" || order.status === "invoiced" || order.status === "done") && (
                <>
                  <ActionBtn onClick={issueInvoice} disabled={busy} testid="admin-issue-invoice" primary={!order.invoice_number}>
                    <Receipt className="h-4 w-4" /> {order.invoice_number ? "Přegenerovat fakturu" : "Vystavit fakturu"}
                  </ActionBtn>
                  {order.invoice_number && (
                    <ActionBtn onClick={() => openPdf("invoice")} testid="admin-open-invoice">
                      <Receipt className="h-4 w-4" /> Otevřít fakturu PDF
                    </ActionBtn>
                  )}
                </>
              )}
              {order.status !== "done" && order.status !== "cancelled" && (
                <ActionBtn onClick={markDone} disabled={busy} testid="admin-mark-done">
                  <CheckCircle2 className="h-4 w-4" /> Označit jako vyřízené
                </ActionBtn>
              )}
              <ActionBtn onClick={genMagic} testid="admin-magic-link">
                <Link2 className="h-4 w-4" /> Vygenerovat magic link
              </ActionBtn>
              {order.status !== "cancelled" && (
                <ActionBtn onClick={cancel} disabled={busy} testid="admin-cancel" danger>
                  Zrušit objednávku
                </ActionBtn>
              )}
            </div>
          </div>

          {magic && (
            <div className="rounded-3xl border border-amber-300/20 bg-white/[0.03] p-5">
              <div className="text-xs uppercase tracking-[0.18em] text-amber-300/90 font-semibold">Magic link</div>
              <div className="mt-2 text-xs text-slate-400">Pošlete klientovi tento odkaz (e-mail integrace bude doplněna). Platnost 7 dní.</div>
              <div className="mt-3 rounded-lg border border-white/10 bg-[#0A0D17] p-2 text-xs font-mono break-all text-amber-200" data-testid="admin-magic-url">
                {magic.url}
              </div>
              <button onClick={() => copyText(magic.url, "magic")} className="mt-2 inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200">
                <Copy className="h-3.5 w-3.5" /> {copied === "magic" ? "Zkopírováno" : "Kopírovat"}
              </button>
            </div>
          )}

          {err && (
            <div className="rounded-xl border border-rose-400/30 bg-rose-400/10 p-3 text-xs text-rose-200 flex items-start gap-2" data-testid="admin-action-error">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {err}
            </div>
          )}
        </aside>
      </div>
    </Shell>
  );
}

// -------- shared UI --------
function Shell({ children, auth }) {
  // auth flag is currently informational only
  void auth;
  return (
    <div className="min-h-screen bg-[#0A0D17] text-slate-200">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8">{children}</div>
    </div>
  );
}
function Header({ onLogout }) {
  return (
    <div className="flex items-center justify-between mb-6">
      <Link to="/admin/objednavky" className="flex items-center gap-3" data-testid="admin-home">
        <span className="block h-10 w-10 rounded-xl bg-[#0A0D17] ring-1 ring-amber-300/20"
          style={{ backgroundImage: "url(/brand/logo.png)", backgroundRepeat: "no-repeat", backgroundSize: "150% auto", backgroundPosition: "50% 18%" }} />
        <div>
          <div className="font-heading text-base font-bold text-white">STK Komfort · Admin</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300/80">Objednávky</div>
        </div>
      </Link>
      <button onClick={onLogout} data-testid="admin-logout"
        className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] px-3 py-2 text-xs text-white">
        <LogOut className="h-4 w-4" /> Odhlásit
      </button>
    </div>
  );
}
function Th({ children, className = "" }) {
  return <th className={`text-left font-semibold px-4 py-3 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}
function StatusBadge({ status, large }) {
  const color = STATUS_COLOR[status] || "slate";
  const colorMap = {
    amber: "bg-amber-300/15 border-amber-300/40 text-amber-200",
    emerald: "bg-emerald-400/15 border-emerald-400/40 text-emerald-200",
    blue: "bg-sky-400/15 border-sky-400/40 text-sky-200",
    rose: "bg-rose-400/15 border-rose-400/40 text-rose-200",
    slate: "bg-white/[0.04] border-white/10 text-slate-300",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 ${large ? "py-1.5 text-xs" : "py-0.5 text-[11px]"} font-semibold ${colorMap[color]}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}
function ActionBtn({ children, onClick, disabled, testid, primary, danger }) {
  const cls = primary
    ? "bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 text-slate-900"
    : danger
      ? "border border-rose-400/30 bg-rose-400/10 hover:bg-rose-400/20 text-rose-200"
      : "border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-white";
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
      className={`w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${cls}`}>
      {children}
    </button>
  );
}
function Field({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{k}</div>
      <div className="mt-0.5 text-sm text-white break-words">{v}</div>
    </div>
  );
}
